export enum MANAGER_TASK_WORKFLOW_STATUS {
	TODO = 'TODO',
	IN_PROGRESS = 'IN_PROGRESS',
	DONE = 'DONE',
	VOIDED = 'VOIDED',
}

export enum MANAGER_TASK_EVALUATION_STATUS {
	PENDING = 'PENDING',
	SUCCESS = 'SUCCESS',
	FAILED = 'FAILED',
}

export enum MANAGER_TASK_PRIORITY {
	LOW = 'LOW',
	MEDIUM = 'MEDIUM',
	HIGH = 'HIGH',
	CRITICAL = 'CRITICAL',
}

export type RecurringPattern = 'NEVER' | 'DAILY' | 'WEEKDAYS' | 'WEEKLY' | 'MONTHLY_DAY' | 'MONTHLY_LAST_WEEKDAY'

export type WeekDayKey = 'SUN' | 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT'

export type ManagerTaskRecurrenceIntent =
	| { action: 'UNCHANGED' }
	| {
		action: 'UPSERT_TEMPLATE'
		pattern: 'DAILY' | 'WEEKDAYS' | 'WEEKLY' | 'MONTHLY_DAY'
		firstOccurrenceAt: string
		weekDays?: WeekDayKey[]
		reminderOffsetHours?: number | null
	}
	| { action: 'DEACTIVATE_TEMPLATE' }

export enum MANAGER_TASK_FREQUENCY {
	DAILY = 'DAILY',
	WEEKLY = 'WEEKLY',
	MONTHLY = 'MONTHLY',
}

export interface ManagerTaskUser {
	id: number
	firstName?: string | null
	lastName?: string | null
	email?: string | null
	role?: string | null
}

export enum LABEL_LINKED_ENTITY_TYPE {
	JOB = 'JOB',
	FINISH = 'FINISH',
	RATING = 'RATING',
	PAYSLIP = 'PAYSLIP',
	USER = 'USER',
	PRICE_OFFER = 'PRICE_OFFER',
	COMPLAINT = 'COMPLAINT',
}

export interface ManagerTaskLabelAssignmentData {
	jobId?: number | null
	finishId?: number | null
	ratingId?: number | null
	payslipId?: number | null
	userId?: number | null
	priceOfferId?: number | null
	complaintId?: number | null
	dataName?: string | null
}

export interface ManagerTaskLabel {
	id: number
	name: string
	color?: string | null
	linkedEntityType?: LABEL_LINKED_ENTITY_TYPE | null
	managerTaskLabelAssignments?: ManagerTaskLabelAssignmentData | null
}

export interface ManagerTaskComment {
	id: number
	body: string
	createdAt?: string | null
	author?: ManagerTaskUser | null
}

export interface ManagerTaskActivityLog {
	id: number
	action: string
	metadata?: Record<string, unknown> | null
	createdAt?: string | null
	actor?: ManagerTaskUser | null
}

export interface ManagerTask {
	id: number
	title: string
	description?: string | null
	type?: 'MANUAL' | 'SYSTEM' | 'RECURRING' | null
	workflowStatus: MANAGER_TASK_WORKFLOW_STATUS
	evaluationStatus: MANAGER_TASK_EVALUATION_STATUS
	priority: MANAGER_TASK_PRIORITY
	assignedToUserId: number
	createdByUserId?: number | null
	deadlineAt?: string | null
	startAt?: string | null
	recurringPattern?: RecurringPattern | null
	recurringWeekDays?: string[] | null
	reminderOffsetHours?: number | null
	recurringTemplateId?: number | null
	occurrenceDate?: string | null
	occurrenceKey?: string | null
	completedAt?: string | null
	slaHours?: number | null
	completionWindowHours?: number | null
	fixedCompletion: boolean
	predecessorTaskId: number | null
	successorTaskId: number | null
	penaltyAmount?: number | null
	labels?: ManagerTaskLabel[]
	comments?: ManagerTaskComment[]
	activityLogs?: ManagerTaskActivityLog[]
	assignedTo?: ManagerTaskUser | null
	createdBy?: ManagerTaskUser | null
	createdAt?: string | null
	updatedAt?: string | null
}

export interface ManagerTaskPayload {
	title: string
	description?: string | null
	workflowStatus?: MANAGER_TASK_WORKFLOW_STATUS
	priority: MANAGER_TASK_PRIORITY
	assignedToUserId: number
	deadlineAt?: string | null
	startAt?: string | null
	recurringPattern?: RecurringPattern | null
	recurringWeekDays?: string[] | null
	reminderOffsetHours?: number | null
	slaHours?: number | null
	completionWindowHours?: number | null
	penaltyAmount?: number | null
	labelIds?: number[]
	recurrenceIntent?: ManagerTaskRecurrenceIntent
	recurringTemplateId?: number | null
	occurrenceDate?: string | null
	occurrenceKey?: string | null
}

export interface ManagerRecurringTaskTemplate {
	id: number
	title: string
	description?: string | null
	frequency: MANAGER_TASK_FREQUENCY
	workingDaysOnly: boolean
	timeWindowFrom?: string | null
	timeWindowTo?: string | null
	assignedToUserId: number
	slaHours?: number | null
	completionWindowHours?: number | null
	fixedCompletion: boolean
	penaltyAmount?: number | null
	priority: MANAGER_TASK_PRIORITY
	isActive: boolean
	assignedTo?: ManagerTaskUser | null
	createdBy?: ManagerTaskUser | null
	createdByUserId?: number | null
	weekDays?: string | null
	monthDay?: number | null
	deadlineTime?: string | null
	startDate?: string | null
	labels?: ManagerTaskLabel[]
}

export interface ManagerRecurringTaskTemplatePayload {
	title: string
	description?: string | null
	frequency: MANAGER_TASK_FREQUENCY
	workingDaysOnly: boolean
	timeWindowFrom?: string | null
	timeWindowTo?: string | null
	assignedToUserId: number
	slaHours?: number | null
	completionWindowHours?: number | null
	fixedCompletion?: boolean
	penaltyAmount?: number | null
	priority: MANAGER_TASK_PRIORITY
	isActive: boolean
	weekDays?: string | null
	monthDay?: number | null
	deadlineTime?: string | null
	startDate?: string | null
	labelIds?: number[]
}

export interface ManagerRecurringTaskTemplateFilters {
	isActive?: boolean
}

export interface ManagerTaskListResponse {
	data: ManagerTask[]
	meta: {
		total: number
		page: number
		limit: number
	}
}

export interface ManagerTaskFilters {
	status?: MANAGER_TASK_WORKFLOW_STATUS
	evaluationStatus?: MANAGER_TASK_EVALUATION_STATUS
	priority?: MANAGER_TASK_PRIORITY
	labelId?: number
	recurringTemplateId?: number
	assignedToUserId?: number
	dateFrom?: string
	dateTo?: string
	page?: number
	limit?: number
}
